import NiceModal from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import Button from 'components/field/Button';
import TextArea from 'components/field/TextArea';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Modal from 'components/global/Modal';
import Tabs from 'components/global/Tabs';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import Table from 'components/table/Table';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';

import { ADMIN } from 'services/consts';

import AppLogger from 'utils/AppLogger';

const CompareRoleModal = NiceModal.create(() => {
	const axiosPrivate = useAxiosPrivate();
	const { showError } = useAlert();
	const [missingLiveData, setMissingLiveData] = useState(null);
	const [missingStageData, setMissingStageData] = useState(null);
	const [diffData, setDiffData] = useState(null);
	const [loading, setLoading] = useState(false);

	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					token: yup.string().nullable().required()
				})
				.required(),
		[]
	);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			token: window.localStorage.getItem('compareRolesToken') ?? null
		}
	});

	const { handleSubmit } = formMethods;

	const onSubmit = async (data) => {
		setLoading(true);

		let liveRoles, stageRoles;

		await axiosPrivate
			.post(ADMIN.ROLE.GET_ROLES_LIST, {
				paging: { offset: 0, count: 10000 },
				totalRecord: false
			})
			.then((response) => {
				stageRoles = response.data.data;
			})
			.catch(() => {
				showError({
					message: 'Failed to get roles from staging'
				});
			});

		await axiosPrivate
			.post(
				ADMIN.ROLE.GET_ROLES_LIST,
				{
					paging: { offset: 0, count: 10000 },
					totalRecord: false
				},
				{
					baseURL: 'https://app.tryoto.com',
					headers: {
						Authorization: `Bearer ${data.token}`
					}
				}
			)
			.then((response) => {
				window.localStorage.setItem('compareRolesToken', data.token);
				liveRoles = response.data.data;
			})
			.catch(() => {
				showError({
					message: 'Failed to get roles from live'
				});
			});

		if (!liveRoles || !stageRoles) {
			setLoading(false);
			showError({
				message: 'Failed to get roles from live or staging'
			});
			return;
		}

		const missingLive = [];
		const missingStage = [];
		const diff = [];

		liveRoles.forEach((liveRole) => {
			const stageRole = stageRoles.find((stageRole) => stageRole.path === liveRole.path);
			if (!stageRole) {
				missingLive.push(liveRole);
			}
		});
		stageRoles.forEach((stageRole) => {
			const liveRole = liveRoles.find((liveRole) => liveRole.path === stageRole.path);
			if (!liveRole) {
				missingStage.push(stageRole);
			} else {
				delete stageRole.ID;
				delete liveRole.ID;

				if (JSON.stringify(stageRole) !== JSON.stringify(liveRole)) {
					diff.push({
						stage: stageRole,
						live: liveRole
					});
				}
			}
		});

		setMissingLiveData(missingLive);
		setMissingStageData(missingStage);
		setDiffData(diff);
		setLoading(false);
	};

	const defaultColSpan = {
		md: 12
	};

	const tabs = useMemo(
		() => [
			{
				label: `Roles Diff (${diffData?.length})`,
				component: () => <RolesDiffTab data={diffData} />
			},
			{
				label: `Not In Stage Roles (${missingLiveData?.length})`,
				component: () => <MissingLiveRolesTab data={missingLiveData} />
			},
			{
				label: `Not In Live Roles (${missingStageData?.length})`,
				component: () => <MissingStageRolesTab data={missingStageData} />
			}
		],
		[diffData, missingLiveData, missingStageData]
	);

	return (
		<Modal maxWidth="no-max-width" fullwidth className="modal-compareRoleModal">
			<ModalTitle>
				<Typography variant="p1">compareRoles</Typography>
			</ModalTitle>

			<ModalContent>
				{diffData && missingLiveData && missingStageData && !loading ? (
					<Tabs data={tabs} />
				) : (
					<Form formMethods={formMethods}>
						<FormItem colSpan={defaultColSpan} label="Live Access Token" loading={loading} required>
							<TextArea name="token" maxLength="4096" rows="8" />
						</FormItem>
					</Form>
				)}
			</ModalContent>

			<ModalActions>
				<Button
					label="compare"
					onClick={handleSubmit(onSubmit, (errors) => {
						AppLogger.log(errors);
					})}
					loading={loading}
					disabled={diffData && missingLiveData && missingStageData}
				/>
			</ModalActions>
		</Modal>
	);
});

const MissingLiveRolesTab = ({ data }) => {
	const columns = useMemo(
		() => [
			{
				header: 'path',
				accessorKey: 'path',
				size: 200
			},
			{
				header: 'Roles',
				accessorKey: 'roles',
				size: 200,
				renderCell: ({ row }) => <Typography>{row?.original?.roles?.join(', ')}</Typography>
			},
			{
				header: 'Modules',
				accessorKey: 'modules',
				size: 200,
				renderCell: ({ row }) => <Typography>{row?.original?.modules?.join(', ')}</Typography>
			},
			{
				header: 'Disable if module exists',
				accessorKey: 'disableModules',
				size: 200,
				renderCell: ({ row }) => <Typography>{row?.original?.disableModules?.join(', ')}</Typography>
			},
			{
				header: 'Packages',
				accessorKey: 'packageRoleIds',
				size: 200,
				renderCell: ({ row }) => <Typography>{row?.original?.packageRoleIds?.join(', ')}</Typography>
			},
			{
				header: 'Countries',
				accessorKey: 'enabledCountries',
				size: 200,
				renderCell: ({ row }) => <Typography>{row?.original?.enabledCountries?.join(', ')}</Typography>
			},
			{
				header: 'Clients',
				accessorKey: 'clients',
				size: 200,
				renderCell: ({ row }) => <Typography>{row?.original?.clients?.join(', ')}</Typography>
			}
		],
		[]
	);

	return (
		<div
			style={{
				maxHeight: '70dvh',
				overflow: 'auto'
			}}>
			<Table data={data} columns={columns} />
		</div>
	);
};

const MissingStageRolesTab = ({ data }) => {
	const columns = useMemo(
		() => [
			{
				header: 'path',
				accessorKey: 'path'
			},
			{
				header: 'Roles',
				accessorKey: 'roles',
				renderCell: ({ row }) => <Typography>{row?.original?.roles?.join(', ')}</Typography>
			},
			{
				header: 'Modules',
				accessorKey: 'modules',
				renderCell: ({ row }) => <Typography>{row?.original?.modules?.join(', ')}</Typography>
			},
			{
				header: 'Disable if module exists',
				accessorKey: 'disableModules',
				renderCell: ({ row }) => <Typography>{row?.original?.disableModules?.join(', ')}</Typography>
			},
			{
				header: 'Packages',
				accessorKey: 'packageRoleIds',
				renderCell: ({ row }) => <Typography>{row?.original?.packageRoleIds?.join(', ')}</Typography>
			},
			{
				header: 'Countries',
				accessorKey: 'enabledCountries',
				renderCell: ({ row }) => <Typography>{row?.original?.enabledCountries?.join(', ')}</Typography>
			},
			{
				header: 'Clients',
				accessorKey: 'clients',
				renderCell: ({ row }) => <Typography>{row?.original?.clients?.join(', ')}</Typography>
			}
		],
		[]
	);

	return (
		<div
			style={{
				maxHeight: '70dvh',
				overflow: 'auto'
			}}>
			<Table data={data} columns={columns} />
		</div>
	);
};

const RolesDiffTab = ({ data }) => {
	const columns = useMemo(
		() => [
			{
				header: 'path',
				accessorKey: 'live.path'
			},
			{
				header: 'Roles',
				accessorKey: 'roles',
				renderCell: ({ row }) => {
					const liveRow = row?.original?.live?.roles?.join(', ');
					const stageRow = row?.original?.stage?.roles?.join(', ');

					return liveRow === stageRow ? (
						<Typography>{liveRow}</Typography>
					) : (
						<div
							style={{
								display: 'flex',
								flexDirection: 'column',
								gap: '0.5rem',
								overflow: 'auto'
							}}>
							<Typography color="green" fullWidth>
								{liveRow}
							</Typography>
							<Typography color="blue" fullWidth>
								{stageRow}
							</Typography>
						</div>
					);
				}
			},
			{
				header: 'Modules',
				accessorKey: 'modules',
				renderCell: ({ row }) => {
					const liveRow = row?.original?.live?.modules?.join(', ');
					const stageRow = row?.original?.stage?.modules?.join(', ');

					return liveRow === stageRow ? (
						<Typography>{liveRow}</Typography>
					) : (
						<div
							style={{
								display: 'flex',
								flexDirection: 'column',
								gap: '0.5rem',
								overflow: 'auto'
							}}>
							<Typography color="green" fullWidth>
								{liveRow}
							</Typography>
							<Typography color="blue" fullWidth>
								{stageRow}
							</Typography>
						</div>
					);
				}
			},
			{
				header: 'Disable if module exists',
				accessorKey: 'disableModules',
				renderCell: ({ row }) => {
					const liveRow = row?.original?.live?.disableModules?.join(', ');
					const stageRow = row?.original?.stage?.disableModules?.join(', ');

					return liveRow === stageRow ? (
						<Typography>{liveRow}</Typography>
					) : (
						<div
							style={{
								display: 'flex',
								flexDirection: 'column',
								gap: '0.5rem',
								overflow: 'auto'
							}}>
							<Typography color="green" fullWidth>
								{liveRow}
							</Typography>
							<Typography color="blue" fullWidth>
								{stageRow}
							</Typography>
						</div>
					);
				}
			},
			{
				header: 'Packages',
				accessorKey: 'packageRoleIds',
				renderCell: ({ row }) => {
					const liveRow = row?.original?.live?.packageRoleIds?.join(', ');
					const stageRow = row?.original?.stage?.packageRoleIds?.join(', ');

					return liveRow === stageRow ? (
						<Typography>{liveRow}</Typography>
					) : (
						<div
							style={{
								display: 'flex',
								flexDirection: 'column',
								gap: '0.5rem',
								overflow: 'auto'
							}}>
							<Typography color="green" fullWidth>
								{liveRow}
							</Typography>
							<Typography color="blue" fullWidth>
								{stageRow}
							</Typography>
						</div>
					);
				}
			},
			{
				header: 'Countries',
				accessorKey: 'enabledCountries',
				renderCell: ({ row }) => {
					const liveRow = row?.original?.live?.enabledCountries?.join(', ');
					const stageRow = row?.original?.stage?.enabledCountries?.join(', ');

					return liveRow === stageRow ? (
						<Typography>{liveRow}</Typography>
					) : (
						<div
							style={{
								display: 'flex',
								flexDirection: 'column',
								gap: '0.5rem',
								overflow: 'auto'
							}}>
							<Typography color="green" fullWidth>
								{liveRow}
							</Typography>
							<Typography color="blue" fullWidth>
								{stageRow}
							</Typography>
						</div>
					);
				}
			},
			{
				header: 'Clients',
				accessorKey: 'clients',
				renderCell: ({ row }) => {
					const liveRow = row?.original?.live?.clients?.join(', ');
					const stageRow = row?.original?.stage?.clients?.join(', ');

					return liveRow === stageRow ? (
						<Typography>{liveRow}</Typography>
					) : (
						<div
							style={{
								display: 'flex',
								flexDirection: 'column',
								gap: '0.5rem',
								overflow: 'auto'
							}}>
							<Typography color="green" fullWidth>
								{liveRow}
							</Typography>
							<Typography color="blue" fullWidth>
								{stageRow}
							</Typography>
						</div>
					);
				}
			}
		],
		[]
	);

	return (
		<div
			style={{
				maxHeight: '70dvh',
				overflow: 'auto'
			}}>
			<Table data={data} columns={columns} />
		</div>
	);
};

export default CompareRoleModal;
